import { createCSVImportCallback } from "@/src/utils/csvBoxUtility.js";
import DashboardService from "@/src/api/DashboardService.js";
import { message } from "ant-design-vue";
import { transformCustomProperties } from "@/src/utils/utils.js";
import TicketsService from "@/src/api/TicketsService.js";
import { extractDetailedErrorMessage } from "@/src/utils/errorUtils.js";
const CSV_SHEET_KEYS = {
  contacts: {
    development: "1IUeXdrF77GLdYHSx36MWJBwvUG83y",
    production: "Jh02jgbhVzFe3diJ3QmSGEUV0W7OQO",
    india: "LGy2TENDIdhOj8vm588m1bHvGx8Zpb",
  },
  companies: {
    development: "X9VfRc6613Ubi3UNGXmLcouK2MNNjt",
    production: "yHFhTQQNr4iCjhTxh38q6AhRZgn1Su",
    india: "BfCJatuCxDNZhZ9bx2tltlYgLEwbEZ",
  },
  deals: {
    development: "ou1ZWX9XNf9GK4iljAq1orYjY1HYyR",
    production: "SCBoGxE28e8M7BWI5usKmIsr1QDUP4",
    india: "Xbw58lkfoK50VMoWdjmnbgIokcVFQp",
  },
  leads: {
    development: "WGZTOmaO2gqI4MEoJirkIDUPj1RFMa",
    production: "Mv1JNvn6IAVHsL0KYlD7a3sCHTdUln",
    india: "RHXqxiePiJgakbi3BfaSr261YLXUFv",
  },
  campaigns: {
    staging:"miQSz2KB1UNRgI9Cn2uYHvxya8gkCQ",
    development: "B0UUdDAzy4PY9h0Yc0p0VEnRVC8GUd",
    production: "05Oy2MjHC60NpRYzicON9UrD3A4Ap1",
    india: "KeglywrFCdIPi87P5Nikye6S8DSClE",
  },
  dynamic: {
    development: "4pt9RfeUT5GQtzLz4P6AnwDkxaqH1X",
    production: "dFXnC80azycBVvb7ejvQ9dBvEuNNgw",
    india: "QRbzqVg0PgwyvFlo6j3GbjQIjcU7p4",
  },
  dynamic_contacts: {
    development: "YQuPCe3ouMLCWLQzD0Cl0t2WYDCYxI",
    production: "UGUWWyGB8Dp6Ho1aDGymN6zlXr29OH",
  },
  dynamic_companies: {
    development: "SmNKZI4kCij1MQFdh2idXoZD6RfR4p",
    production: "CGVRu0qCY780mabboua4PC28piRBrj",
  },
  dynamic_leads: {
    development: "RyDKXvP5DBjB17vQ7uD2s4EHadsk8B",
    production: "FI469Y9kYIIzOG1gKsgG0YZZKYt3d0",
  },
  dynamic_deals: {
    development: "EKvjEiriZGebHHPe4fIJ15BToVqm7I",
    production: "NtPuaaGipwoQvrTR1cpEZDoFh1JVw7",
  },
};

// Map entity types (lowercase plural) to reference format (PascalCase singular)
const ENTITY_TYPE_TO_REFERENCE = {
  contacts: "Contact",
  companies: "Company",
  deals: "Deal",
  leads: "Lead",
};

const SKIP_TYPES = ["formula", "file", "relation_record", "property_sync", "select_timestamp", "action"];
const DEALS_SKIP_SLUGS = ["last_activity", "deal_stage"];
const shouldSkipAttribute = (slug, type, entityType) => {
  if (SKIP_TYPES.includes(type?.toLowerCase())) return true;
  const isDeal = ["deal", "deals"].includes(entityType?.toLowerCase());
  if (isDeal && DEALS_SKIP_SLUGS.includes(slug?.toLowerCase())) return true;
  return false;
};

export const getCSVSheetKey = (type) => {
  const isIndia = window.location.href.includes("in.superagi.com");

  // Check for India production environment
  if (isIndia) {
    return CSV_SHEET_KEYS[type]?.india;
  }

  const environment =
    process.env.NODE_ENV === "production" ? "production" : "development";
  return CSV_SHEET_KEYS[type][environment];
};

/**
 * Fetches and transforms custom properties for CSV import configuration
 * @param {string} entityType - Entity type in reference format (e.g., "Contact", "Company", "Lead", "Deal")
 * @returns {Promise<Object>} Config object with dynamicColumns for CSV importer
 */
export const fetchCustomPropertiesConfig = async (entityType) => {
  try {
    const response = await DashboardService.fetchCustomProperties(entityType);
    const properties = response.success ? response.custom_properties || [] : [];

    if (properties.length > 0) {
      const filtered = properties.filter((prop) => {
        const datatype = (prop.datatype || prop.type || "text").toLowerCase();
        return !shouldSkipAttribute(prop.slug, datatype, entityType);
      });
      return {
        dynamicColumns: transformCustomProperties(filtered),
      };
    }
    return {};
  } catch (error) {
    console.warn(`Failed to fetch custom properties for ${entityType}:`, error);
    return {};
  }
};

export const setupCSVImporter = async (
  csvBoxState,
  type = "contacts",
  entityListId,
  onSuccessCallback,
  source = null,
) => {
  try {
    await csvBoxState.initializeCSVModal();

    let csvboxConfig = {};
    const referenceType = ENTITY_TYPE_TO_REFERENCE[type];
    if (referenceType) {
      csvboxConfig = await fetchCustomPropertiesConfig(referenceType);
    }

    const importCallback = createCSVImportCallback(
      type,
      DashboardService.importViaCsvbox,
      (successMessage, data) => {
        message.success(successMessage);
        if (onSuccessCallback) onSuccessCallback(successMessage, data);
      },
      (errorMessage) => message.error(errorMessage),
      entityListId,
      null,
      {},
      source,
    );

    const sheetKey = getCSVSheetKey(type);
    csvBoxState.createImporter(sheetKey, csvboxConfig, importCallback);
  } catch (error) {
    console.error("Failed to initialize CSV importer:", error);
    message.error("Failed to initialize CSV importer. Please try again later.");
  }
};

const STANDARD_ENTITY_SHEET_MAP = {
  contacts: "dynamic_contacts",
  companies: "dynamic_companies",
  leads: "dynamic_leads",
  deals: "dynamic_deals",
  campaigns: "campaigns",
};

const SCHEMA_TYPE_OVERRIDE = {
  campaigns: "leads",
};

export const setupDynamicCSVImporter = async (
  csvBoxState,
  objectType,
  onSuccessCallback = null,
  onErrorCallback = null,
  entityListId = -1,
) => {
  const sheetType = STANDARD_ENTITY_SHEET_MAP[objectType] || "dynamic";
  const sheetKey = getCSVSheetKey(sheetType);
  if (!sheetKey) {
    message.error("CSV sheet key not found");
    return;
  }
  try {
    await csvBoxState.initializeCSVModal();

    const schemaObjectType = SCHEMA_TYPE_OVERRIDE[objectType] || objectType;
    const schemaResponse =
      await DashboardService.getObjectSchemaForCSVBox(schemaObjectType);
    if (!schemaResponse.success) {
      throw new Error(schemaResponse.error || "Failed to fetch object schema");
    }

    if (!schemaResponse.dynamic_columns || !Array.isArray(schemaResponse.dynamic_columns) || schemaResponse.dynamic_columns.length === 0) {
      throw new Error("No dynamic columns in schema response");
    }
    
    const dynamicColumns = schemaResponse.dynamic_columns.map((col) => ({
      column_name: col.key,
      display_label: col.title,
      matching_keywords: col.key,
      type: col.type,
      required: col.required || false,
      ...(col.description && { info_hint: col.description }),
      ...(col.validators && { validators: col.validators }),
    }));

    const config = {
      dynamicColumns: dynamicColumns,
    };

    const importCallback = async (result, data) => {
      if (result) {
        if (data.import_id) {
          try {
            const isStandardEntity = !!STANDARD_ENTITY_SHEET_MAP[objectType];
            const importResult = isStandardEntity
              ? await DashboardService.importViaCsvbox(
                  objectType,
                  data.import_id,
                  data.row_success,
                  entityListId,
                )
              : await DashboardService.importViaCsvboxV2({
                  object_type: objectType,
                  import_id: data.import_id,
                  row_count: data.row_success,
                  mapping_config: {},
                  ...(entityListId && entityListId !== -1 ? { entity_list_id: entityListId } : {}),
                });

            if (importResult.success) {
              message.success(importResult.message);
              if (onSuccessCallback) onSuccessCallback(importResult.message, data);
            } else {
              const errMsg = extractDetailedErrorMessage(importResult, "Failed to import");
              message.error(errMsg);
              if (onErrorCallback) onErrorCallback(errMsg);
            }
          } catch (error) {
            const errMsg = extractDetailedErrorMessage(error, `Error importing ${objectType}`);
            message.error(errMsg);
            if (onErrorCallback) onErrorCallback(errMsg);
          }
        } else {
          message.error(
            `Failed to start ${objectType} import: Missing import ID`,
          );
        }
      } else {
        message.error(`Failed to upload the CSV file for ${objectType}`);
        if (onErrorCallback) onErrorCallback("Failed to upload CSV file");
      }
    };

    const importer = csvBoxState.createImporter(
      sheetKey,
      config,
      importCallback,
    );

    return importer;
  } catch (error) {
    console.error(
      "Failed to initialize CSV importer. Please try again later.",
      error,
    );
    if (onErrorCallback) onErrorCallback(error.message);
    throw error;
  }
};

export const setupTicketsCSVImporter = async (
  csvBoxState,
  onSuccessCallback = null,
  onErrorCallback = null,
) => {
  const sheetKey = getCSVSheetKey("dynamic");
  if (!sheetKey) {
    message.error("CSV sheet key for tickets not set");
    return;
  }
  try {
    await csvBoxState.initializeCSVModal();

    const schemaResponse = await TicketsService.getCSVSchema();
    if (!schemaResponse?.success) {
      throw new Error(schemaResponse?.error || "Failed to fetch ticket schema");
    }

    const config = {};

    // Build columns ONLY from schema.attributes (match DynamicCRM behavior)
    const attributeColumnsFromSchema = Array.isArray(
      schemaResponse.schema?.attributes,
    )
      ? transformCustomProperties(
          schemaResponse.schema.attributes
            .filter((attr) => {
              const t = String(attr.datatype || attr.type || "").toLowerCase();
              return t !== "relationship" && t !== "action";
            })
            .map((attr) => ({
              slug: attr.slug,
              name: attr.name || attr.slug,
              datatype: attr.datatype || attr.type,
            })),
        )
      : [];
    config.dynamicColumns = attributeColumnsFromSchema;

    const importCallback = async (result, data) => {
      if (result) {
        if (data.import_id) {
          try {
            const payload = {
              import_id: data.import_id,
              row_count: data.row_success,
              mapping_config: {},
            };
            const importResult = await TicketsService.importCSV(payload);
            if (importResult?.success) {
              message.success(importResult.message || "Import Initiated");
              if (onSuccessCallback) onSuccessCallback(importResult.message);
            } else {
              const errMsg = extractDetailedErrorMessage(importResult, "Failed to import tickets");
              message.error(errMsg);
              if (onErrorCallback) onErrorCallback(errMsg);
            }
          } catch (error) {
            if (error.response?.status === 402 || error.response?.data?.payment_required) {
              const errMsg =
                error.response?.data?.error ||
                "Insufficient credits to import tickets";
              message.error(errMsg);
              if (onErrorCallback) onErrorCallback(errMsg);
            } else {
              message.error(`Error importing tickets: ${error.message}`);
              if (onErrorCallback) onErrorCallback(error.message);
            }
          }
        } else {
          const msg = "Failed to start tickets import: Missing import ID";
          message.error(msg);
          if (onErrorCallback) onErrorCallback(msg);
        }
      } else {
        const msg = "Failed to upload the CSV file for tickets";
        message.error(msg);
        if (onErrorCallback) onErrorCallback(msg);
      }
    };

    csvBoxState.createImporter(sheetKey, config, importCallback);
  } catch (error) {
    console.error("Failed to initialize Tickets CSV importer:", error);
    message.error("Failed to initialize CSV importer. Please try again later.");
    if (onErrorCallback) onErrorCallback(error.message);
    throw error;
  }
};
